import React, { useState, useEffect, useRef, useMemo } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../shared/heading";

const { Chart: ChartJS, registerables } = Chart;
ChartJS.register(...registerables);

const ReusableKPIChartV2 = ({
  chartData,
  title,
  subtitle,
  chartTitle,
  yAxisLabel = "",
  colors = {
    revenue: "00B0F0",
    transaction: "#E80909",
    atv: "#A5A5A5",
    als: "#EEB60A",
  },
  labels = {
    revenue: "Monthly Revenue",
    transaction: "Monthly Transaction",
    atv: "ATV",
    als: "ALS",
  },
  showDataLabels = true,
  showRamadanRow = false,
  ramadanBorderColor = "#4C96D9",
  transparentRevenueLabel = false,
  alsTextColor = null,
  enableRevenueShadow = false,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const [tableMetrics, setTableMetrics] = useState({ firstColWidth: 180, tableWidth: null });



  const formatSmart = (value) => {
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
};

  const themeColors = {
    textColor: "#e5e7eb",
    gridColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "#4b5563",
    backgroundColor: "#6B6A6C",
    labelBoxBg: "rgba(0, 0, 0, 0.4)",
    labelTextColor: "#ffffff",
    tooltipBg: "rgba(31, 41, 55, 0.95)",
    tooltipBorder: "#4b5563",
  };

  const yAxisFormatters = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        left: (value) => value,
        right: (value) => value,
      };
    }
    return {
      left: (value) => value.toLocaleString(),
      right: (value) => (value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)),
    };
  }, [chartData]);

  const axisRanges = useMemo(() => {
  if (!chartData || chartData.length === 0)
    return { y: { min: undefined, max: undefined }, y1: { min: undefined, max: undefined } };

  const pad = (vals, factor) => {
    const filtered = vals.filter((v) => v != null && !isNaN(v));
    if (!filtered.length) return { min: undefined, max: undefined };
    const mn = Math.min(...filtered);
    const mx = Math.max(...filtered);
    const range = mx - mn || Math.abs(mx) * 0.2 || 1;
    return { min: Math.max(0, Math.floor(mn - range * factor)), max: Math.ceil(mx + range * factor) };
  };

  const yRange = pad(
    [...chartData.map((d) => d.revenue)],
    0.25
  );

  const atvVals = chartData.map((d) => d.atv).filter((v) => v != null && !isNaN(v));
  const alsVals = chartData.map((d) => d.als).filter((v) => v != null && !isNaN(v));
  const txnVals = chartData.map((d) => d.transaction).filter((v) => v != null && !isNaN(v));

  const allRightVals = [...atvVals, ...alsVals, ...txnVals];
  const rightMax = allRightVals.length ? Math.max(...allRightVals) : 0;
  const rightMin = allRightVals.length ? Math.min(...allRightVals) : 0;

  const range = rightMax - rightMin || rightMax * 0.2 || 1;
  const y1Max = Math.ceil(rightMax + range * 0.25);
  const y1Min = Math.max(0, Math.floor(rightMin - range * 0.1));

  return { y: yRange, y1: { min: y1Min, max: y1Max } };
}, [chartData]);

  const groupDataByYear = (data) => {
    const grouped = {};
    data.forEach((item) => {
      if (!grouped[item.year]) grouped[item.year] = [];
      grouped[item.year].push(item);
    });
    return grouped;
  };

  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !chartData || chartData.length === 0) return [];
    const ramadanData = [];
    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };
    chartData.forEach((item) => {
      const monthName = item.month.split("-")[0];
      const year = parseInt(item.year);
      const monthNum = monthMap[monthName] !== undefined ? monthMap[monthName] : 0;
      const hijriYear = year - 579;
      const ramadanFirstDay = moment(`${hijriYear}-9-1`, "iYYYY-iM-iD");
      const ramadanStartGregMonth = ramadanFirstDay.month();
      const ramadanStartGregYear = ramadanFirstDay.year();
      const isRamadan = ramadanStartGregYear === year && ramadanStartGregMonth === monthNum;
      ramadanData.push({ isRamadan, monthName, year });
    });
    return ramadanData;
  }, [chartData, showRamadanRow]);

  useEffect(() => {
    if (chartData.length === 0 || !chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");

    const revenueLabelPositions = [];
    const finalLabelRects = [];

    const ramadanMonthIndices = new Set();
    if (showRamadanRow && getRamadanMonths.length > 0) {
      getRamadanMonths.forEach((info, idx) => {
        if (info.isRamadan) ramadanMonthIndices.add(idx);
      });
    }

    const groupedByYear = groupDataByYear(chartData);
    const yearRanges = Object.keys(groupedByYear).map((year) => ({
      year,
      span: groupedByYear[year].length,
    }));
    const monthNames = chartData.map((d) => d.month.split("-")[0]);
    const ramadanFlags = getRamadanMonths.map((r) => r.isRamadan);

    // ─── customLegendPlugin ──────────────────────────────────────────────────
    const customLegendPlugin = {
      id: "customLegend",
      afterDraw(chart) {
        const legend = chart.legend;
        if (!legend || !legend.legendItems) return;
        const ctx = chart.ctx;
        ctx.save();
        legend.legendItems.forEach((item, i) => {
          const hitBox = legend.legendHitBoxes && legend.legendHitBoxes[i];
          if (!hitBox) return;
          const centerY = hitBox.top + hitBox.height / 2;
          const iconW = 22;
          const iconX = hitBox.left;
          ctx.clearRect(iconX, hitBox.top, iconW + 2, hitBox.height);
          if (i === 0) {
            ctx.fillStyle = colors.revenue;
            ctx.fillRect(iconX, centerY - 5, iconW, 10);
          } else if (i === 1) {
            ctx.strokeStyle = colors.transaction;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.fillStyle = colors.transaction;
            ctx.beginPath();
            ctx.arc(iconX + iconW / 2, centerY, 4, 0, Math.PI * 2);
            ctx.fill();
          } else if (i === 2) {
            ctx.strokeStyle = colors.atv;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
          } else if (i === 3) {
            ctx.strokeStyle = colors.als;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([5, 4]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        });
        ctx.restore();
      },
    };

    // ─── shadowPlugin ────────────────────────────────────────────────────────
    const shadowPlugin = {
      id: "shadowPlugin",
      beforeDatasetDraw(chart, args) {
        const { ctx } = chart;
        const dsIndex = args.index;
        ctx.save();
        if (dsIndex === 0 && enableRevenueShadow) {
          ctx.shadowColor = colors.revenue + "99";
          ctx.shadowBlur = 25;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 8;
        } else if (dsIndex === 1) {
          ctx.shadowColor = colors.transaction + "BB";
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
        } else if (dsIndex === 2) {
          ctx.shadowColor = "#ffffff";
          ctx.shadowBlur = 14;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        } else if (dsIndex === 3) {
          ctx.shadowColor = colors.als + "CC";
          ctx.shadowBlur = 12;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
        }
      },
     afterDatasetDraw(chart, args) {
  const { ctx } = chart;

  // reset shadow pehle
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;

  // ✅ sirf revenue bars ke liye
  if (args.index === 0) {
    const meta = chart.getDatasetMeta(0);

    ctx.save();

    // 🔥 glow settings
    ctx.shadowColor = colors.revenue ;
    ctx.shadowBlur = 10;

    ctx.strokeStyle = colors.revenue;
    ctx.lineWidth = 1.5;

    meta.data.forEach((bar) => {
      const { x, y, base, width } = bar.getProps(
        ["x", "y", "base", "width"],
        true
      );

      const left = x - width / 2;
      const height = base - y;

      // 👇 sirf border draw ho raha hai
      ctx.strokeRect(left, y, width, height);
    });

    ctx.restore();
  }
}
    };

    // ─── revenueLabelPlugin ──────────────────────────────────────────────────
    const revenueLabelPlugin = {
  id: "revenueLabels",
  afterDatasetsDraw(chart) {
    if (!showDataLabels) return;
    revenueLabelPositions.length = 0;

    const revenueDataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden) return;

    const ctx = chart.ctx;
    const yAxis = chart.scales.y;
    const chartBottom = yAxis.bottom;

    ctx.save();

    // Figure out spacing between adjacent bars so we know if labels need
    // to be shrunk / staggered to avoid overlapping each other.
    const barXPositions = meta.data.map((bar) => bar.x);
    const avgSpacing =
      barXPositions.length > 1
        ? Math.min(
            ...barXPositions.slice(1).map((x, i) => x - barXPositions[i])
          )
        : 200;

    // Shrink font when bars are packed tightly (lots of months).
    const fontSize = avgSpacing < 42 ? 8 : avgSpacing < 55 ? 9 : 11;
    const rowGap = fontSize <= 9 ? 16 : 22;

    let lastRowRight = { 0: -Infinity, 1: -Infinity };

    meta.data.forEach((bar, index) => {
      const value = revenueDataset.data[index];
      if (value == null || value === 0) return;
      if (ramadanMonthIndices.has(index)) return;

      const xPos = bar.x;
      const barTop = bar.y;
      const barBottom = chartBottom;
      const barHeight = barBottom - barTop;

      const text = value.toLocaleString();
      ctx.font = `bold ${fontSize}px Arial`;
      const textWidth = ctx.measureText(text).width;
     const boxWidth = Math.max(fontSize <= 9 ? 28 : 38, textWidth + 6);
const boxHeight = fontSize <= 9 ? 14 : 18;

      // Decide which row (0 = upper, 1 = lower) avoids clashing with the
      // previous label placed in that row.
      let row = 0;
      if (xPos - boxWidth / 2 < lastRowRight[0] + 4) {
        row = xPos - boxWidth / 2 < lastRowRight[1] + 4 ? 0 : 1;
      }
      lastRowRight[row] = xPos + boxWidth / 2;

      // Always place label INSIDE bar, just below the top, staggered by row.
      const boxY = barTop + 6 + row * rowGap;
      const boxX = xPos - boxWidth / 2;

      // Only draw if bar is tall enough
      if (barHeight < boxHeight + 10 + row * rowGap) return;

      revenueLabelPositions.push({
        x: xPos, y: boxY + boxHeight / 2,
        width: boxWidth, height: boxHeight,
        top: boxY, bottom: boxY + boxHeight,
        left: boxX, right: boxX + boxWidth,
      });

      ctx.shadowColor = colors.revenue + "99";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 2;
      ctx.strokeStyle = colors.revenue;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 4);
      ctx.stroke();

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      if (!transparentRevenueLabel) {
        ctx.fillStyle = themeColors.labelBoxBg;
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
      }
      ctx.fillStyle = themeColors.labelTextColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, xPos, boxY + boxHeight / 2);
    });

    ctx.restore();
  },
};

    // ─── lineLabelPlugin ─────────────────────────────────────────────────────
    const lineLabelPlugin = {
  id: "lineLabels",
  afterDatasetsDraw(chart) {
    if (!showDataLabels) return;

    const ctx = chart.ctx;
    const padding = 5;
    const chartTop = chart.scales.y.top;
    const chartBottom = chart.scales.y.bottom;

    // Collect all labels
    const labelsList = [];

    chart.data.datasets.forEach((dataset, dsIndex) => {
      if (dsIndex === 0) return;
      const meta = chart.getDatasetMeta(dsIndex);
      if (!meta || !meta.data || meta.hidden) return;

      meta.data.forEach((point, pointIndex) => {
        if (dsIndex === 1 && ramadanMonthIndices.has(pointIndex)) return;

        const value = dataset.data[pointIndex];
        if (value == null || value === 0) return;

        let text = "";
        let datasetType = "";
        if (dsIndex === 1) { text = Math.round(value).toLocaleString(); datasetType = "transaction"; }
        else if (dsIndex === 2) { if (value === 0) return; text = formatSmart(value); datasetType = "atv"; }
else if (dsIndex === 3) { if (value === 0) return; text = formatSmart(value) + "%"; datasetType = "als"; }
        if (!text) return;

     ctx.font = "bold 9px Arial";
        const textWidth = ctx.measureText(text).width;

        const isSmallBox = dsIndex === 1 || dsIndex === 3;
        const extraWidth = isSmallBox ? -2 : 4;
        const paddingMultiplier = isSmallBox ? 2 : 4;

        const boxWidth = textWidth + padding * paddingMultiplier + extraWidth;
        const boxHeight = isSmallBox ? 16 : 22;

        // dsIndex 1 (transaction) = above point, 2/3 = below
const preferBelow = dsIndex === 2 || dsIndex === 3;; // sirf ATV neeche, Occupancy upar

        const initialOffsetY = preferBelow ? 14 : -14;

        labelsList.push({
          x: point.x,
          y: point.y + initialOffsetY,
          originalY: point.y,
          text,
          color: dataset.borderColor || "#888",
          width: boxWidth,
          height: boxHeight,
          datasetIndex: dsIndex,
          pointIndex,
          datasetType,
          preferBelow,
          visible: true,
        });
      });
    });

    // Sort by x position
    labelsList.sort((a, b) => a.x - b.x);

    const verticalPadding = 4;
    const horizontalPadding = 6;

    const getRect = (lbl) => ({
      left:   lbl.x - lbl.width / 2,
      right:  lbl.x + lbl.width / 2,
      top:    lbl.y - lbl.height / 2,
      bottom: lbl.y + lbl.height / 2,
    });

    const checkOverlap = (rect1, rect2) =>
      !(
        rect1.right  + horizontalPadding < rect2.left  - horizontalPadding ||
        rect1.left   - horizontalPadding > rect2.right + horizontalPadding ||
        rect1.bottom + verticalPadding   < rect2.top   - verticalPadding   ||
        rect1.top    - verticalPadding   > rect2.bottom + verticalPadding
      );

    // Multi-pass collision resolution
    const MAX_PASSES = 5;
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      let anyMoved = false;

      for (let i = 0; i < labelsList.length; i++) {
        const curr = labelsList[i];
        if (!curr.visible) continue;

        // Check against revenue labels first
        for (const revLabel of revenueLabelPositions) {

  if (checkOverlap(getRect(curr), revLabel)) {

    // ATV (Price per Sq Mtr) — slightly below revenue
   if (curr.datasetIndex === 2) {
      curr.y = revLabel.bottom + curr.height / 4 + 4;
    } 
    else if (curr.preferBelow) {
      curr.y = revLabel.bottom + curr.height + 8;
    } 
    else {
      curr.y = revLabel.top - curr.height - 16;
    }

    anyMoved = true;
  }
}

        // Check against all other visible labels
        for (let j = 0; j < labelsList.length; j++) {
          if (i === j) continue;
          const other = labelsList[j];
          if (!other.visible) continue;

          if (!checkOverlap(getRect(curr), getRect(other))) continue;

          anyMoved = true;

          // Same dataset — hide the overlapping one
          if (curr.datasetIndex === other.datasetIndex) {

  // NEVER hide ATV (Price per Sq Mtr)
  if (curr.datasetIndex === 2) {
    continue;
  }

  const currDist = Math.abs(curr.y - curr.originalY);
  const otherDist = Math.abs(other.y - other.originalY);

  if (currDist > otherDist) {
    curr.visible = false;
  } else {
    other.visible = false;
  }

  break;
}

          // Different datasets — push apart
          if (i > j) {
            // curr resolves against other
            if (curr.preferBelow) {
              curr.y = getRect(other).bottom + curr.height / 2 + verticalPadding + 2;
            } else {
              curr.y = getRect(other).top - curr.height / 2 - verticalPadding - 2;
            }
          }
        }

        // Clamp within chart bounds
        if (curr.visible) {
          const halfH = curr.height / 2;
          curr.y = Math.max(
            chartTop  + halfH + 2,
            Math.min(chartBottom - halfH - 2, curr.y)
          );
        }
      }

      if (!anyMoved) break;
    }

    // Build finalLabelRects
    finalLabelRects.length = 0;
    revenueLabelPositions.forEach((pos) => {
      finalLabelRects.push({
        left: pos.left - 6, right: pos.right + 6,
        top:  pos.top  - 6, bottom: pos.bottom + 6,
      });
    });
    labelsList.forEach((lbl) => {
      if (!lbl.visible) return;
      finalLabelRects.push({
        left:   lbl.x - lbl.width  / 2 - 6,
        right:  lbl.x + lbl.width  / 2 + 6,
        top:    lbl.y - lbl.height / 2 - 6,
        bottom: lbl.y + lbl.height / 2 + 6,
      });
    });

    // Draw labels
    labelsList.forEach((lbl) => {
      if (!lbl.visible) return;

      const boxX = lbl.x - lbl.width / 2;
      const boxY = lbl.y - lbl.height + 10;

      ctx.save();

      if (lbl.datasetType === "transaction" || lbl.datasetType === "als") {
        ctx.shadowColor   = lbl.color + "99";
        ctx.shadowBlur    = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.strokeStyle   = lbl.color;
        ctx.lineWidth     = 1;
        ctx.strokeRect(boxX, boxY, lbl.width, lbl.height);
        ctx.shadowColor   = "transparent";
        ctx.shadowBlur    = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

     ctx.fillStyle    = lbl.datasetType === "als" ? lbl.color : themeColors.labelTextColor;
      ctx.textAlign    = "center";
      ctx.textBaseline = "middle";
      ctx.font         = (lbl.datasetType === "transaction" || lbl.datasetType === "als") ? " 9px Arial" : " 12px Arial";
      ctx.fillText(lbl.text, lbl.x, lbl.y);
      ctx.restore();
    });

    // Draw connector lines
    labelsList.forEach((lbl) => {
      if (!lbl.visible) return;

      const originalPoint = chart.getDatasetMeta(lbl.datasetIndex).data[lbl.pointIndex];
      if (!originalPoint) return;

      ctx.save();
      const labelEdgeY = lbl.preferBelow
        ? lbl.y - lbl.height / 2
        : lbl.y + lbl.height / 2;

      if (Math.abs(originalPoint.y - labelEdgeY) > 5) {
        ctx.beginPath();
        ctx.strokeStyle = lbl.color;
        ctx.lineWidth   = 1;
        ctx.moveTo(lbl.x, labelEdgeY);
        ctx.lineTo(originalPoint.x, originalPoint.y);
        ctx.stroke();
      }
      ctx.restore();
    });
  },
};

    // ─── ramadanPlugin ────────────────────────────────────────────────────────
    const ramadanPlugin = (() => {
      const hasRamadan = showRamadanRow && ramadanFlags.some(Boolean);
      if (!hasRamadan) return { id: "ramadanPlugin_disabled", afterDraw() {} };

      return {
        id: "ramadanPlugin",
        afterDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          const BOX_H = 24;
          const BOX_W = 165;
          const BR    = 5;
          const ARROW_W = 16;
          const H_PAD = 8;
          const V_PAD = 8;

          const chartTop    = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft   = xAxis.left;
          const chartRight  = xAxis.right;

          const clampX = (x) =>
            Math.max(chartLeft + 4, Math.min(chartRight - BOX_W - 4, x));
          const clampY = (y) =>
            Math.max(chartTop + 4, Math.min(chartBottom - BOX_H - 4, y));

          const mkRect = (l, t, w, h) => ({
            left: l, right: l + w, top: t, bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left   - hPad < b.right  + hPad &&
            a.right  + hPad > b.left   - hPad &&
            a.top    - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top    - vPad;

          const obstacles = [];

          finalLabelRects.forEach((r) => obstacles.push({ ...r }));

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const bL   = props.x - props.width / 2;
                const bR   = props.x + props.width / 2;
                const bTop = Math.min(props.y, props.base);
                const bBot = Math.max(props.y, props.base);
                if (!isNaN(bL) && !isNaN(bTop)) {
                  obstacles.push({ left: bL - 3, right: bR + 3, top: bTop - 3, bottom: bBot + 3 });
                }
              } catch (_) {}
            });
          });

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type === "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((pt) => {
              if (!pt) return;
              const px = pt.x, py = pt.y;
              if (px == null || py == null || isNaN(px) || isNaN(py)) return;
              obstacles.push({ left: px - 8, right: px + 8, top: py - 8, bottom: py + 8 });
            });
          });

          const getBarTopY = (idx) => {
            const meta = chart.getDatasetMeta(0);
            if (meta?.data?.[idx]) {
              const bar = meta.data[idx];
              try {
                const props = bar.getProps(["y", "base"], true);
                return Math.min(props.y, props.base ?? props.y);
              } catch (_) {
                return Math.min(bar.y, bar.base ?? bar.y);
              }
            }
            for (let i = 1; i < chart.data.datasets.length; i++) {
              const m = chart.getDatasetMeta(i);
              if (m.hidden) continue;
              if (m?.data?.[idx] && !isNaN(m.data[idx].y)) return m.data[idx].y;
            }
            return yAxis.getPixelForValue(0);
          };

          const items = [];
          let mi = 0;
          yearRanges.forEach((yr) => {
            for (let i = 0; i < yr.span; i++) {
              if (ramadanFlags[mi]) {
                const val    = chartData[mi]?.revenue ?? 0;
                const dataX  = xAxis.getPixelForValue(mi);
                const dataY  = getBarTopY(mi);
                items.push({
                  label:   `RAMADHAN ${yr.year} ${monthNames[mi]} : ${val.toLocaleString()}`,
                  dataX,
                  dataY,
                  revenue: val,
                });
              }
              mi++;
            }
          });

          if (!items.length) return;

          const buildCandidates = () => {
            const result = [];
            const vertGaps   = [8, 20, 36, 55, 78, 105, 135, 170, 210, 255, 300, 350];
            const horizShifts = [0, -20, 20, -45, 45, -75, 75, -110, 110, -150, 150, -200, 200];
            for (const dy of vertGaps) {
              for (const dx of horizShifts) {
                result.push([dx, dy]);
              }
            }
            return result;
          };

          const candidateOffsets = buildCandidates();
          const placedBoxes = [];

          items.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;

            let chosenX = null;
            let chosenY = null;
            let placed  = false;

            for (const [dx, dy] of candidateOffsets) {
              const candidateBoxX = clampX(dataX + dx - BOX_W / 2);
              const candidateBoxY = clampY(arrowTipY - dy - BOX_H);

              const testRect = mkRect(candidateBoxX, candidateBoxY, BOX_W, BOX_H);

              const allObs = [...obstacles, ...placedBoxes];
              const blocked = allObs.some((obs) => overlaps(testRect, obs));

              if (!blocked) {
                chosenX = candidateBoxX;
                chosenY = candidateBoxY;
                placed  = true;
                break;
              }
            }

            if (!placed) {
              let bestScore = Infinity;
              let bestX = clampX(dataX - BOX_W / 2);
              let bestY = chartTop + 6;

              for (const [dx, dy] of candidateOffsets.slice(0, 60)) {
                const bx = clampX(dataX + dx - BOX_W / 2);
                const by = clampY(arrowTipY - dy - BOX_H);
                const r  = mkRect(bx, by, BOX_W, BOX_H);
                const allObs = [...obstacles, ...placedBoxes];
                const score = allObs.filter((obs) => overlaps(r, obs, 0, 0)).length;
                if (score < bestScore) {
                  bestScore = score;
                  bestX = bx;
                  bestY = by;
                }
              }
              chosenX = bestX;
              chosenY = bestY;
            }

            const chosenRect = mkRect(chosenX, chosenY, BOX_W, BOX_H);
            placedBoxes.push(chosenRect);
            obstacles.push({
              left:   chosenRect.left   - H_PAD,
              right:  chosenRect.right  + H_PAD,
              top:    chosenRect.top    - V_PAD,
              bottom: chosenRect.bottom + V_PAD,
            });

            const bTop = chosenY;
            const bBot = chosenY + BOX_H;

            const baseCX = Math.min(
              Math.max(dataX, chosenX + ARROW_W / 2 + BR),
              chosenX + BOX_W - ARROW_W / 2 - BR
            );
            const baseBL = baseCX - ARROW_W / 2;
            const baseBR = baseCX + ARROW_W / 2;

            const drawShape = () => {
              ctx.beginPath();
              ctx.moveTo(chosenX + BR, bTop);
              ctx.lineTo(chosenX + BOX_W - BR, bTop);
              ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
              ctx.lineTo(chosenX + BOX_W, bBot);
              ctx.lineTo(baseBR, bBot);
              ctx.lineTo(dataX, arrowTipY);
              ctx.lineTo(baseBL, bBot);
              ctx.lineTo(chosenX, bBot);
              ctx.lineTo(chosenX, bTop + BR);
              ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
              ctx.closePath();
            };

            ctx.save();

            ctx.shadowColor = "rgba(255, 215, 0, 0.6)";
            ctx.shadowBlur  = 12;
            ctx.strokeStyle = ramadanBorderColor || "#ffdd57";
            ctx.lineWidth   = 0.5;
            drawShape();
            ctx.stroke();

            ctx.shadowColor = "transparent";
            ctx.shadowBlur  = 0;

            ctx.fillStyle = "transparent";
            drawShape();
            ctx.fill();

            ctx.strokeStyle = ramadanBorderColor || "#ffdd57";
            ctx.lineWidth   = 1.4;
            drawShape();
            ctx.stroke();

            ctx.fillStyle    = "#ffffff";
            ctx.textAlign    = "center";
            ctx.textBaseline = "middle";
            ctx.font         = "bold 8.5px Arial";
            ctx.fillText(label, chosenX + BOX_W / 2, chosenY + BOX_H / 2);

            ctx.restore();
          });
        },
      };
    })();

    const syncTableToChart = () => {
      const chart = chartInstance.current;
      if (!chart || !chart.chartArea || !chartRef.current || !tableRef.current) return;
      const canvasRect = chartRef.current.getBoundingClientRect();
      const tableRect = tableRef.current.getBoundingClientRect();
      const plotLeft = canvasRect.left + chart.chartArea.left;
      const plotRight = canvasRect.left + chart.chartArea.right;
      const firstColWidth = Math.max(0, plotLeft - tableRect.left);
      const tableWidth = firstColWidth + Math.max(0, plotRight - plotLeft);
      setTableMetrics((prev) =>
        prev.firstColWidth === firstColWidth && prev.tableWidth === tableWidth
          ? prev
          : { firstColWidth, tableWidth }
      );
    };

    chartInstance.current = new ChartJS(ctx, {
      type: "bar",
      data: {
        labels: chartData.map((d) => d.month),
        datasets: [
          {
            type: "bar",
            label: labels.revenue,
            data: chartData.map((d) => d.revenue),
            borderColor: colors.revenue,
            backgroundColor:'transparent',
            borderWidth: 1,
            maxBarThickness: 24,
            categoryPercentage: 1.0,
            barPercentage: 0.9,
            yAxisID: "y",
          },
         {
  type: "line",
  label: labels.transaction,
  data: chartData.map((d) => d.transaction),
  borderColor: colors.transaction,
  backgroundColor: colors.transaction,
  borderWidth: 1.5,
  pointRadius: 0,
  pointBackgroundColor: colors.transaction,
  borderDash: [6, 4],       // ← yeh add karo
  borderDashOffset: 0,   // ← yeh bhi
  yAxisID: "y1",
},
          {
            type: "line",
            label: labels.atv,
            data: chartData.map((d) => d.atv),
            borderColor: colors.atv,
            backgroundColor: colors.atv,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.atv,
            yAxisID: "y1",
          },
          {
            type: "line",
            label: labels.als,
            // data: chartData.map((d) => d.als),
            data: chartData.map((d)  =>d.als  ),
            borderColor: colors.als,
            backgroundColor: colors.als,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.als,
            yAxisID: "y1",
            borderDash: [6, 4],
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
        interaction: { mode: null },
        hover: { mode: null },
        events: [],
        onResize: () => requestAnimationFrame(syncTableToChart),
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#ffffff",
              usePointStyle: false,
              boxWidth: 24,
              boxHeight: 12,
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  datasetIndex: i,
                  hidden: false,
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  lineWidth: 0,
                  fontColor: "#ffffff",
                })),
            },
          },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            offset: true,
            grid: { display: false },
            ticks: { display: false, color: themeColors.textColor, font: { size: 12 } },
          },
          y: {
            type: "linear",
            position: "left",
            min: axisRanges.y.min,
            max: axisRanges.y.max,
            ticks: { color: themeColors.textColor, font: { size: 12 }, callback: yAxisFormatters.left },
            grid: { display: false },
          },
          y1: {
            type: "linear",
            position: "right",
            min: axisRanges.y1.min,
            max: axisRanges.y1.max,
            ticks: { color: themeColors.textColor, font: { size: 12 }, callback: yAxisFormatters.right },
            grid: { display: false },
          },
        },
        layout: { padding: { left: 30, right: 30, top: 0, bottom: 0 } },
      },
      plugins: [customLegendPlugin, shadowPlugin, revenueLabelPlugin, lineLabelPlugin, ramadanPlugin],
    });

    requestAnimationFrame(syncTableToChart);

    const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstance.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstance.current.resize(w, h);
  requestAnimationFrame(syncTableToChart);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstance.current) chartInstance.current.destroy();
};
  }, [
    chartData, colors, labels, showDataLabels,
    yAxisFormatters, showRamadanRow, ramadanBorderColor,
    getRamadanMonths, axisRanges,
    transparentRevenueLabel, enableRevenueShadow,
  ]);

  if (chartData.length === 0) return null;

  const groupedChartData = groupDataByYear(chartData);
  const months = chartData.map((d) => d.month.split("-")[0]);
  const yearRanges = Object.keys(groupedChartData).map((year) => ({
    year,
    span: groupedChartData[year].length,
  }));

  const RevenueIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: colors.revenue, borderRadius: 1 }} />
    </span>
  );

  const TransactionIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle", position: "relative", width: 20, height: 10 }}>
      <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 2, backgroundColor: colors.transaction, transform: "translateY(-50%)" }} />
      <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 6, height: 6, borderRadius: "50%", backgroundColor: colors.transaction }} />
    </span>
  );

  const ATVIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 18, height: 2, backgroundColor: colors.atv }} />
    </span>
  );

  const ALSIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <svg width="20" height="10" viewBox="0 0 20 10">
        <line x1="0" y1="5" x2="20" y2="5" stroke={colors.als} strokeWidth="2" strokeDasharray="4,3" />
      </svg>
    </span>
  );

  return (
    <div className="w-full h-full flex items-center justify-center px-4">
      <div
        className="w-full mx-auto px-8 py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div
          className="text-center"
          style={{ backgroundColor: "#ffffff", padding: "0.1rem 2rem 0.25rem 2rem" }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        <div
          className="w-full p-3"
          style={{ background: "radial-gradient(ellipse at center, #525254 0%, #3a3a3c 45%, #1e1e1f 100%)", borderRadius: "8px" }}
        >
          <div className="rounded-t-lg pb-0" style={{ paddingLeft: "50px", paddingRight: "2px", paddingTop: "16px" }}>
            <h3 className="text-center text-base font-semibold mb-0" style={{ color: "#ffffff" }}>{chartTitle}</h3>
            <div className="ml-3 h-[380px] relative" ref={containerRef}>
              {yAxisLabel && (
                <div style={{ position: "absolute", left: -30, top: "50%", transform: "translateY(-50%) rotate(-90deg)", color: "#ffffff", fontSize: "10px", fontWeight: "bold", whiteSpace: "nowrap", transformOrigin: "center center" }}>
                  {yAxisLabel}
                </div>
              )}
              <canvas ref={chartRef}></canvas>
            </div>
          </div>

          <div className="w-full p-3 pb-6" style={{ marginLeft: "-20px", overflow: "visible" }}>
            <div style={{ overflow: "visible", width: "100%" }}>
              <table
                ref={tableRef}
                className="!border-collapse text-xs table-fixed"
                style={{
                  tableLayout: "fixed",
                  width: tableMetrics.tableWidth ? `${tableMetrics.tableWidth}px` : "100%",
                }}
              >
                <colgroup>
                  <col style={{ width: `${tableMetrics.firstColWidth}px` }} />
                  {months.map((_, idx) => (
                    <col key={idx} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="py-0.5 px-1 text-center font-normal"></th>
                    {months.map((month, idx) => (
                      <th key={idx} className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[9px]" style={{ color: "#ffffff", borderColor: "#4b5563" }}>{month}</th>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-0.5 px-1 text-center text-[9px]"></td>
                    {yearRanges.map((range, idx) => (
                      <td key={idx} colSpan={range.span} className="!border !border-border px-0.5 py-0.5 text-center text-[9px] font-bold" style={{ color: "#ffffff", borderColor: "#4b5563" }}>{range.year}</td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="!border !border-border px-1 py-1 font-normal text-[8px] text-left whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      <RevenueIndicator />{labels.revenue}
                    </td>
                    {chartData.map((item, idx) => (
                      <td key={idx} className="!border !border-border px-0.5 py-1 text-center text-[7px] whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {item.revenue != null ? item.revenue.toLocaleString() : ""}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="!border !border-border px-1 py-1 font-normal text-[8px] text-left whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      <TransactionIndicator />{labels.transaction}
                    </td>
                    {chartData.map((item, idx) => (
                      <td key={idx} className="!border !border-border px-0.5 py-1 text-center text-[7px] whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {item.transaction != null ? item.transaction.toLocaleString() : ""}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="!border !border-border px-1 py-1 font-normal text-[8px] text-left whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      <ATVIndicator />{labels.atv}
                    </td>
                    {chartData.map((item, idx) => (
                      <td key={idx} className="!border !border-border px-0.5 py-1 text-center text-[7px] whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {item.atv != null ? formatSmart(item.atv) : ""}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="!border !border-border px-1 py-1 font-normal text-[8px] text-left whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      <ALSIndicator />{labels.als}
                    </td>
                    {chartData.map((item, idx) => (
                      <td key={idx} className="!border !border-border px-0.5 py-1 text-center text-[7px] whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {item.als != null ? formatSmart(item.als) + "%" : ""}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
              <div className="text-center py-0.5 mt-2 text-[9px] font-bold" style={{ color: "#ffffff" }}>Months</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReusableKPIChartV2;